import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { authService } from '../services/auth';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<'SUCCESS' | 'NEW_PASSWORD_REQUIRED'>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  isSpedflowAdmin: boolean;
  isTenantAdmin: boolean;
  isAdvocate: boolean;
  isClient: boolean;
}

interface PasswordChallengeData {
  email: string;
  session: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordChallenge, setPasswordChallenge] = useState<PasswordChallengeData | null>(null);

  const checkAuth = useCallback(async () => {
    if (!authService.isAuthenticated()) {
      setLoading(false);
      return;
    }

    try {
      const userData = await authService.getMe();
      setUser(userData);
    } catch {
      // Token invalid, clear it
      localStorage.removeItem('idToken');
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email: string, password: string): Promise<'SUCCESS' | 'NEW_PASSWORD_REQUIRED'> => {
    setError(null);
    try {
      const response = await authService.login(email, password);
      
      // Check if this is a password change challenge
      if ('challengeName' in response && response.challengeName === 'NEW_PASSWORD_REQUIRED') {
        setPasswordChallenge({ email: response.email, session: response.session });
        return 'NEW_PASSWORD_REQUIRED';
      }
      
      // Normal login success - response has user and tokens
      if ('user' in response) {
        setUser(response.user);
      }
      return 'SUCCESS';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    }
  };

  const completeNewPassword = async (newPassword: string) => {
    if (!passwordChallenge) {
      throw new Error('No password challenge in progress');
    }

    setError(null);
    try {
      const response = await authService.completeNewPassword(
        passwordChallenge.email,
        newPassword,
        passwordChallenge.session
      );
      setUser(response.user);
      setPasswordChallenge(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Password change failed';
      setError(message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
      setPasswordChallenge(null);
    }
  };

  const isSpedflowAdmin = user?.role === 'spedflow-admin' || user?.groups?.includes('spedflow-admins') || false;
  const isTenantAdmin = user?.role === 'tenant-admin' || user?.groups?.includes('tenant-admins') || false;
  const isAdvocate = user?.role === 'advocate' || user?.groups?.includes('advocates') || isTenantAdmin;
  const isClient = user?.role === 'client' || user?.groups?.includes('clients') || false;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        completeNewPassword,
        logout,
        isSpedflowAdmin,
        isTenantAdmin,
        isAdvocate,
        isClient,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
